import React, { useEffect } from 'react'
import Menu, { MenuProps } from 'components/menu'
import { useWhatsNew } from 'hooks/whatsNew'
import { WHATS_NEW_TAG_LABELS } from 'app-constants'
import { IWhatsNewEntry, IWhatsNewRelease } from 'types'

const formatReleaseDate = (isoDate: string): string => {
  const parsed = new Date(`${isoDate}T00:00:00`)
  if (Number.isNaN(parsed.getTime())) return isoDate
  return parsed.toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' })
}

// The link is drafted by a model and reviewed by a human; only plain web URLs are ever rendered.
const isSafeLink = (link: string | undefined): link is string => Boolean(link && /^https?:\/\//i.test(link))

const EntryRow: React.FC<{ entry: IWhatsNewEntry }> = ({ entry }) => {
  return (
    <div
      data-testid="whats-new-entry"
      className="flex flex-col gap-1 px-2.5 py-3 border-b border-divider last:border-b-0"
    >
      <div className="flex items-start justify-between gap-2">
        <span className="text-sm text-body font-semibold whitespace-normal">{entry.title}</span>
        <span className="text-xs text-muted bg-surface-sunken rounded px-1.5 py-0.5 whitespace-nowrap flex-shrink-0">
          {WHATS_NEW_TAG_LABELS[entry.tag]}
        </span>
      </div>
      <span className="text-sm text-muted whitespace-normal">
        {entry.body}
        {isSafeLink(entry.link) && (
          <>
            {' '}
            <a className="text-blue hover:underline" href={entry.link} target="_blank" rel="noopener noreferrer">
              Read more
            </a>
          </>
        )}
      </span>
    </div>
  )
}

const ReleaseSection: React.FC<{ release: IWhatsNewRelease }> = ({ release }) => {
  return (
    <div className="pb-1">
      <div
        data-testid="whats-new-release-date"
        className="px-2.5 pt-3 pb-1 text-xs uppercase tracking-wide text-muted font-semibold"
      >
        {formatReleaseDate(release.date)}
      </div>
      {release.entries.map((entry) => (
        <EntryRow key={entry.issue} entry={entry} />
      ))}
    </div>
  )
}

const WhatsNewPanel: React.FC<MenuProps> = ({ id, menuState, setMenuState, width }) => {
  const { releases, markSeen } = useWhatsNew()

  useEffect(() => {
    if (menuState.open) {
      markSeen()
    }
  }, [menuState.open])

  return (
    <Menu id={id} menuState={menuState} setMenuState={setMenuState} width={width} viewportMargin={10} fixed>
      <div className="text-base pt-[5px] pb-2.5">
        <div className="sticky top-0 z-10 bg-surface w-full flex items-center text-body font-bold p-2.5">
          <span>What&apos;s new</span>
        </div>
        {releases.length === 0 && (
          <div className="w-full flex items-center justify-center text-muted text-sm p-5">Nothing new yet</div>
        )}
        <div className="max-h-[70vh] overflow-y-auto">
          {releases.map((release) => (
            <ReleaseSection key={release.id} release={release} />
          ))}
        </div>
      </div>
    </Menu>
  )
}

export default WhatsNewPanel
